import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { Pagination } from '@quero-delivery/quero-components-web';
import { format } from '@querodelivery/qd-date-helper';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router';

import { useSearchParams } from 'react-router-dom';
import * as Form from '@radix-ui/react-form';
import { Button, Text } from '@querodelivery/uni-react';
import styles from './PlacesList.module.scss';

import { DEFAULT_LIMIT_REQUEST, MediaQueryMobile } from '@/Constants';
import JsonView from '@/Shared/JsonView';
import WhatsappNumber from '@/Components/WhatsappNumber/WhatsappNumber';
import type { GetPlacesProps } from '@/_features/places/api/get-places';
import { useGetPlaces } from '@/_features/places/api/get-places';
import { stringifyQueryStringArray } from '@/Utils/FnUtils';
import type { Place } from '@/_types/places';
import { TYPE_DELIVERY_OPTIONS } from '@/_features/deliveries/constants';
import type { TypeFetchPlacesForm } from '../types';
import { fetchPlacesFormSchema } from '../constants';
import { TableSkeleton } from '@/_components/skeletons/table-skeleton/table-skeleton';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import {
  setFilterCityOnPlacesList,
  setFilterDeliveryTypeOnPlacesList,
} from '@/_store/modules/filters/slice';
import { useCities } from '@/_hooks/use-cities';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { TextInput } from '@/_components/text-input/text-input';
import { Table, useStackedTableLayout } from '@/_components/table';
import { TableEmptyState } from '@/_components/table-empty-state/table-empty-state';

const limit = DEFAULT_LIMIT_REQUEST;
const PLACES_TABLE_GRID_COLUMNS = 'repeat(5, minmax(0, 1fr))';
const WORD_BREAK_STYLE: CSSProperties = {
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  minWidth: 0,
};

function getDeliveryTypeLabel(place: Place): string {
  if (place.isDeliverymenLinked && place.isDeliverymenCloud)
    return 'Nuvem e Próprio';
  if (place.isDeliverymenLinked) return 'Próprio';
  if (place.isDeliverymenCloud) return 'Nuvem';
  return '- - -';
}

function PlacesList() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();

  const isStackedTableLayout = useStackedTableLayout({
    customQuery: MediaQueryMobile,
  });

  const filteredCityId = useAppSelector(
    state => state.filters.cityIdOnPlacesList,
  );
  const filteredDeliveryType = useAppSelector(
    state => state.filters.deliveryTypeOnPlacesList,
  );

  useEffect(() => {
    if (filteredCityId) {
      setSearchParams(prev => {
        prev.set('cityId', filteredCityId);
        return prev;
      });
    }

    if (filteredDeliveryType) {
      setSearchParams(prev => {
        prev.set('deliveryType', filteredDeliveryType);
        return prev;
      });
    }
  }, [filteredCityId, filteredDeliveryType]);

  const [params, setParams] = useState<GetPlacesProps>({
    placeName: searchParams.get('name') || undefined,
    cityId: filteredCityId || searchParams.get('cityId') || undefined,
    establishmentId: searchParams.get('establishmentId') || undefined,
    deliveryType:
      filteredDeliveryType || searchParams.get('deliveryType') || undefined,
    page: Number(searchParams.get('page')) || 1,
  });

  const { CITIES_OPTIONS_WITH_ALL, loadingCities } = useCities();

  const {
    getValues,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TypeFetchPlacesForm>({
    resolver: yupResolver(fetchPlacesFormSchema),
    defaultValues: {
      name: params.placeName || '',
      city: params.cityId || '',
      deliveryType: params.deliveryType || '',
      establishmentId: params.establishmentId || '',
    },
  });

  const { data: places, isLoading } = useGetPlaces({
    page,
    ...params,
  });

  const onSubmitPlaceForm: SubmitHandler<TypeFetchPlacesForm> = data => {
    const { city, name, deliveryType, establishmentId } = data;
    setPage(1);

    const queries = {
      name,
      city,
      deliveryType,
      establishmentId,
      page: 1,
    };

    setParams({
      placeName: name || undefined,
      cityId: city || undefined,
      deliveryType: deliveryType || undefined,
      establishmentId: establishmentId || undefined,
      page: 1,
    });
    setSearchParams(stringifyQueryStringArray(queries));
  };

  const navigateToPlaceDetails = (placeId: string) => {
    navigate(`/estabelecimentos/${placeId}`);
  };

  function renderPlaceRow(place: Place) {
    return (
      <Table.Row
        key={place._id}
        gridTemplateColumns={PLACES_TABLE_GRID_COLUMNS}
        onClick={() => {
          navigateToPlaceDetails(place._id);
        }}
      >
        <Table.Cell className={styles.tcPlace}>
          <Text
            fontWeight="semibold"
            tone="supporting"
            style={WORD_BREAK_STYLE}
          >
            {place.nomeExibicao}
          </Text>
          <Text fontWeight="regular" tone="supporting" style={WORD_BREAK_STYLE}>
            ({place._id})
          </Text>
        </Table.Cell>
        <Table.Cell>
          <WhatsappNumber number={place.celular} />
        </Table.Cell>
        <Table.Cell>{`${place?.cityName} - ${place?.cityUf}`}</Table.Cell>
        <Table.Cell>{getDeliveryTypeLabel(place)}</Table.Cell>
        <Table.Cell>
          {format(new Date(place.createdAt), 'dd/MM/yyyy HH:mm')}
        </Table.Cell>
      </Table.Row>
    );
  }

  const renderPlacesListMobileCard = (place: Place) => {
    return (
      <Table.MobileCard
        key={place._id}
        onClick={() => navigateToPlaceDetails(place._id)}
      >
        <Table.MobileItem label="Nome">
          <div>
            <Text
              fontWeight="semibold"
              tone="supporting"
              style={WORD_BREAK_STYLE}
            >
              {place.nomeExibicao}
            </Text>
            <Text
              fontWeight="regular"
              tone="supporting"
              style={WORD_BREAK_STYLE}
            >
              ({place._id})
            </Text>
          </div>
        </Table.MobileItem>
        <Table.MobileItem label="Telefone">
          <WhatsappNumber number={place.celular} />
        </Table.MobileItem>
        <Table.MobileItem label="Cidade">
          {`${place?.cityName} - ${place?.cityUf}`}
        </Table.MobileItem>
        <Table.MobileItem label="Tipo de entrega">
          {getDeliveryTypeLabel(place)}
        </Table.MobileItem>
        <Table.MobileItem label="Data de cadastro">
          {format(new Date(place.createdAt), 'dd/MM/yyyy HH:mm')}
        </Table.MobileItem>
      </Table.MobileCard>
    );
  };

  function handleChangePage(page: number) {
    const { name, city, deliveryType, establishmentId } = getValues();
    setPage(page);

    const queries = {
      page,
      name,
      city,
      deliveryType,
      establishmentId,
    };

    setParams(prev => ({
      ...prev,
      placeName: name || undefined,
      cityId: city || undefined,
      page,
    }));
    setSearchParams(stringifyQueryStringArray(queries));
  }

  return (
    <div className="container">
      <Form.Root onSubmit={handleSubmit(onSubmitPlaceForm)}>
        <div className={styles.filtersContainer}>
          <Controller
            name="establishmentId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                name="establishmentId"
                value={value}
                label="Id do estabelecimento"
                onChange={onChange}
                autoFocus
                errorMessage={errors.establishmentId?.message}
                error={!!errors.establishmentId?.message}
              />
            )}
          />
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                name="name"
                value={value}
                label="Nome"
                onChange={onChange}
                autoFocus
                errorMessage={errors.name?.message}
                error={!!errors.name?.message}
              />
            )}
          />
          <Controller
            name="city"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                clearable
                search
                value={value}
                label="Município"
                placeholder="Filtre o município"
                options={CITIES_OPTIONS_WITH_ALL}
                loading={loadingCities}
                onChange={data => {
                  dispatch(setFilterCityOnPlacesList(data as string));
                  onChange(data as string);
                }}
                errorMessage={errors.city?.message}
                error={!!errors.city?.message}
              />
            )}
          />
          <Controller
            name="deliveryType"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                value={value}
                label="Tipo de entrega"
                placeholder="Filtre pelo tipo"
                clearable
                options={TYPE_DELIVERY_OPTIONS}
                onChange={value => {
                  dispatch(setFilterDeliveryTypeOnPlacesList(value as string));
                  onChange(value as string);
                }}
                errorMessage={errors.deliveryType?.message}
                error={!!errors.deliveryType?.message}
              />
            )}
          />
          <div className={styles.containerButton}>
            <Button
              variant="cta"
              type="submit"
              disabled={isLoading}
              size="lg"
              width="100%"
            >
              buscar
            </Button>
          </div>
        </div>
      </Form.Root>
      <div className={styles.headerPlace} />

      <div className="mgn-top-sm">
        {isLoading ? (
          <TableSkeleton columnCount={5} rowCount={5} />
        ) : places?.data && places.data.length > 0 ? (
          <>
            <Text
              variant="headingMd"
              tone="supporting"
              className={styles.headerLabel}
            >
              {places.totalCount}{' '}
              {places.totalCount === 1 ? 'estabelecimento' : 'estabelecimentos'}
            </Text>
            {isStackedTableLayout ? (
              <Table.MobileList className={styles.placesMobileList}>
                {places.data.map(place => renderPlacesListMobileCard(place))}
              </Table.MobileList>
            ) : (
              <div className={styles.placesTableScroll}>
                <Table className={styles.placesTable}>
                  <Table.Header>
                    <Table.Row gridTemplateColumns={PLACES_TABLE_GRID_COLUMNS}>
                      <Table.HeaderCell>Nome</Table.HeaderCell>
                      <Table.HeaderCell>Telefone</Table.HeaderCell>
                      <Table.HeaderCell>Cidade</Table.HeaderCell>
                      <Table.HeaderCell>Tipo de entrega</Table.HeaderCell>
                      <Table.HeaderCell>Data de cadastro</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {places?.data?.map(place => renderPlaceRow(place))}
                  </Table.Body>
                </Table>
              </div>
            )}
            <div className={styles.pagination}>
              <Pagination
                size="sm"
                limitPerPage={limit}
                currentPage={page}
                totalCount={places.totalCount || 0}
                onPageChange={handleChangePage}
              />
            </div>
          </>
        ) : (
          <TableEmptyState
            title="Nenhum estabelecimento encotrado"
            description="Não encontramos estabelecimentos com os filtros selecionados."
          />
        )}
      </div>

      <footer className="end-page-margin" />

      <JsonView name="Places" source={places} />
    </div>
  );
}

export default PlacesList;
